import { useCallback, useEffect, useRef, useState } from 'react';

import isEqual from 'lodash/isEqual';
import { useTranslation } from 'react-i18next';

export const hasErrors = (errors: (string | string[])[] = []) =>
  errors.filter((item) => {
    if (Array.isArray(item)) {
      return hasErrors(item);
    }

    return Boolean(item);
  }).length !== 0;

const useValidation = <T>(initialValues: T, validationSchema, onSubmit) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState<Partial<T>>({});
  const [isSubmitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);
  const initialDataRef = useRef(initialValues);
  const validationSchemaRef = useRef(validationSchema);
  const { t } = useTranslation();

  useEffect(() => {
    setTouched(!isEqual(values, initialValues));
  }, [values, initialValues]);

  const setErrorManually = useCallback((key: string, value?) => {
    setErrors((old) => ({ ...old, [key]: value }));
  }, []);

  const handleChange = useCallback(
    (e) => {
      const { name, value, type } = e.target;
      setValues((old) => ({ ...old, [name]: type === 'number' && value !== '' ? +value : value }));
      // hide error after changing input value
      setErrorManually(name);
    },
    [setErrorManually],
  );

  const setFieldManually = useCallback(
    (key: string, value) => {
      setValues((old) => ({ ...old, [key]: value }));
      // hide error after changing input value
      setErrorManually(key);
    },
    [setErrorManually],
  );

  const setDataValue = useCallback((values) => setValues(values), []);

  const checkErrors = useCallback(() => {
    if (!validationSchemaRef.current) {
      return false;
    }
    const schemaWithTranslations = validationSchemaRef.current(t);
    const errors = schemaWithTranslations.cast(values);
    setErrors(errors);
  }, [validationSchemaRef, values, t]);

  const handleSubmit = useCallback(() => {
    checkErrors();
    setSubmitting(true);
  }, [checkErrors]);

  const resetToDefault = useCallback(() => setValues(initialDataRef.current), [initialDataRef]);

  const resetErrors = useCallback(() => setErrors({}), []);

  useEffect(() => {
    if (isSubmitting) {
      const noErrors = !hasErrors(Object.values(errors));

      if (noErrors) {
        onSubmit(values, {
          setValue: setFieldManually,
          setDataValue,
          resetToDefault,
        });
      }

      setSubmitting(false);
    }
  }, [errors, isSubmitting, values, onSubmit, setFieldManually, resetToDefault, setDataValue]);

  return {
    handleSubmit,
    handleChange,
    setFieldManually,
    setErrorManually,
    setDataValue,
    resetToDefault,
    resetErrors,
    errors,
    values,
    touched,
  };
};

export default useValidation;
